/**
 * Shared client-side PDF branding helper.
 *
 * Used by every page that generates a PDF in the browser (html2canvas + jsPDF).
 * Each consumer calls `generateBrandedPdf({element, filename, ...})` and gets
 * a PDF with:
 *   - Cover page: tenant logo (or "Your logo here" placeholder) + report title
 *     + tenant company name + generated date.
 *   - Body: chunked capture of the supplied element at scale=2 — never exceeds
 *     browser canvas size limits (~16384 px Chrome, ~11180 px Firefox).
 *   - Last page: platform attribution + AI disclaimer (suppressible).
 *   - Every page footer: company name (left) + "Page X of Y" (right) stamped
 *     as vector text via jsPDF.
 *
 * Backend equivalent: backend/services/pdf_branding.py — same copy, same
 * placement, kept in sync intentionally.
 */
import axios from 'axios';
import { API } from '../App';

export const PLATFORM_ATTRIBUTION =
  'Generated by Ergoniq Platform — a product of SAARV Solutions';
export const AI_DISCLAIMER =
  'This report was generated using AI-assisted tools and may contain inaccuracies. Please verify critical information before acting on it.';

// Module-level cache so multiple PDF exports in a session share one logo fetch.
// `null` = not fetched yet, `''` = fetched and tenant has no logo.
let _logoCache = null;
let _logoCacheTs = 0;
const LOGO_TTL_MS = 15 * 60 * 1000;

/**
 * Fetch the tenant logo as a data: URL via the same-origin backend proxy.
 * Returns `null` if the tenant has no logo (or the fetch failed) so callers
 * can fall back to the placeholder.
 */
export async function fetchTenantLogoDataUrl() {
  const now = Date.now();
  if (_logoCache !== null && now - _logoCacheTs < LOGO_TTL_MS) {
    return _logoCache || null;
  }
  try {
    // NOTE: `API` already includes the `/api` segment (see App.js where it
    // is exported as `${BACKEND_URL}/api`). Older revisions of this file
    // had a second `/api/` here which silently 404'd and poisoned the
    // module-level cache with an empty string for 15 minutes — so the
    // logo never appeared on any client-rendered PDF or report.
    const r = await axios.get(`${API}/tenant-admin/branding/logo-stream`, {
      responseType: 'blob',
    });
    if (!r.data) {
      _logoCache = '';
      _logoCacheTs = now;
      return null;
    }
    const dataUrl = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(r.data);
    });
    _logoCache = dataUrl;
    _logoCacheTs = now;
    return dataUrl;
  } catch (_e) {
    _logoCache = '';
    _logoCacheTs = now;
    return null;
  }
}

/** Reset the cache — useful if the tenant just uploaded a new logo. */
export function invalidateLogoCache() {
  _logoCache = null;
  _logoCacheTs = 0;
}

/**
 * Fallback tenant company name from the cached user payload in localStorage.
 * Returns '' if nothing is found — caller can decide what to render.
 */
export function getCurrentTenantName() {
  try {
    const u = JSON.parse(window.localStorage.getItem('user') || '{}');
    return u.company_name || u.tenant?.name || '';
  } catch {
    return '';
  }
}

function _formatDate(d) {
  try {
    return d.toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  } catch {
    return d.toISOString().split('T')[0];
  }
}

/** Build the default cover-page DOM (returns an HTMLElement). */
function _buildDefaultCover({ logoDataUrl, reportTitle, subtitle, tenantName }) {
  const cover = document.createElement('div');
  cover.style.cssText =
    'text-align:center; padding: 1.4in 0 0.6in 0; min-height: 8in; box-sizing: border-box; page-break-after: always; break-after: page;';

  // Centering note: html2canvas treats <img> as inline and its centering via
  // `text-align:center` on the parent is unreliable when the intrinsic image
  // width is smaller than max-width — the inline baseline can shift it left.
  // Use `display:block; margin:0 auto` on the img directly for deterministic
  // horizontal centering.
  const logoBlock = logoDataUrl
    ? `<div style="width:100%; margin:0 0 30px 0;">
         <img src="${logoDataUrl}" alt="Company logo" style="display:block; margin:0 auto; max-height:90px; max-width:240px; object-fit:contain;" />
       </div>`
    : `<div style="width:240px; height:90px; margin:0 auto 30px auto; border:2px dashed #d1d5db; border-radius:8px; background:#f9fafb; box-sizing:border-box; padding-top:34px; text-align:center;">
         <span style="font-size:14px; font-style:italic; color:#9ca3af;">Your logo here</span>
       </div>`;

  const infoLines = [];
  if (tenantName) {
    infoLines.push(
      `<p style="font-size:18px; color:#111827; font-weight:600; margin:0 0 6px 0;">${_escapeHtml(tenantName)}</p>`,
    );
  }
  infoLines.push(
    `<p style="font-size:12px; color:#6b7280; margin:0;">Generated: ${_formatDate(new Date())}</p>`,
  );

  cover.innerHTML = `
    ${logoBlock}
    ${reportTitle ? `<h1 style="font-size:30px; color:#111827; margin:0 0 8px 0; font-weight:700; letter-spacing:0.02em;">${_escapeHtml(reportTitle)}</h1>` : ''}
    ${subtitle ? `<p style="font-size:16px; color:#6b7280; margin:0 0 24px 0;">${_escapeHtml(subtitle)}</p>` : ''}
    <div style="margin:48px auto 0 auto; padding:18px 24px; background:#f9fafb; border:1px solid #e5e7eb; border-radius:8px; max-width:340px; min-width:260px; box-sizing:border-box; text-align:center;">
      ${infoLines.join('')}
    </div>
  `;
  return cover;
}

/** Build the default last-page footer DOM. */
function _buildDefaultLastPageFooter({ skipAiDisclaimer }) {
  const footer = document.createElement('div');
  footer.style.cssText =
    'margin-top:30px; padding-top:14px; border-top:1px solid #e5e7eb; text-align:center; font-size:10px; color:#6b7280; page-break-inside:avoid;';
  footer.innerHTML = `
    <p style="margin:0 0 4px 0;">${_escapeHtml(PLATFORM_ATTRIBUTION)}</p>
    ${skipAiDisclaimer ? '' : `<p style="margin:0; font-style:italic; color:#9ca3af;">${_escapeHtml(AI_DISCLAIMER)}</p>`}
  `;
  return footer;
}

function _escapeHtml(s) {
  if (s == null) return '';
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Generate a branded PDF from an existing element.
 *
 * @param {Object} opts
 * @param {HTMLElement} opts.element - The body content to capture. It is
 *        cloned so its layout is not disturbed in the live page.
 * @param {string} opts.filename - Output filename, e.g. 'Report.pdf'.
 * @param {string} [opts.reportTitle] - Title shown on the default cover page.
 * @param {string} [opts.subtitle] - Subtitle (e.g. study name) under title.
 * @param {string} [opts.tenantName] - Tenant company name for cover + footer.
 * @param {boolean} [opts.skipCover=false] - Don't prepend a cover page (use
 *        when the body element already includes one).
 * @param {boolean} [opts.skipLastPage=false] - Don't append the last-page
 *        attribution + AI disclaimer.
 * @param {boolean} [opts.skipAiDisclaimer=false] - Suppress the AI disclaimer
 *        line on the last page (e.g. for regulator-facing reports).
 */
export async function generateBrandedPdf({
  element,
  filename,
  reportTitle = '',
  subtitle = '',
  tenantName = '',
  skipCover = false,
  skipLastPage = false,
  skipAiDisclaimer = false,
}) {
  if (!element) throw new Error('generateBrandedPdf: element is required');
  if (!filename) throw new Error('generateBrandedPdf: filename is required');

  // Fall back to the cached user payload's company_name when caller omits it.
  if (!tenantName) tenantName = getCurrentTenantName();

  const logoDataUrl = await fetchTenantLogoDataUrl();

  // Build a hidden wrapper with cover + cloned body + last-page footer.
  const styleId = 'branded-pdf-style';
  const oldStyle = document.getElementById(styleId);
  if (oldStyle) oldStyle.remove();
  const style = document.createElement('style');
  style.id = styleId;
  style.textContent = `
    .branded-pdf-capturing,
    .branded-pdf-capturing > * {
      max-height: none !important;
      overflow: visible !important;
      height: auto !important;
    }
    .branded-pdf-capturing * {
      font-family: Arial, Helvetica, 'Noto Sans', sans-serif !important;
      word-spacing: 0.12em !important;
      letter-spacing: 0.01em !important;
      text-rendering: geometricPrecision !important;
      -webkit-font-smoothing: antialiased !important;
    }
    .branded-pdf-capturing table { border-collapse: collapse !important; page-break-inside: auto !important; }
    .branded-pdf-capturing tr { page-break-inside: avoid !important; }
    .branded-pdf-capturing thead { display: table-header-group !important; }
    .branded-pdf-capturing h1, .branded-pdf-capturing h2, .branded-pdf-capturing h3 {
      page-break-after: avoid !important;
    }
  `;
  document.head.appendChild(style);

  const wrapper = document.createElement('div');
  wrapper.className = 'branded-pdf-capturing';
  wrapper.style.cssText =
    'position:absolute; left:-10000px; top:0; width:900px; background:#ffffff; padding:24px; box-sizing:border-box;';

  if (!skipCover) {
    wrapper.appendChild(
      _buildDefaultCover({ logoDataUrl, reportTitle, subtitle, tenantName }),
    );
  }
  // Clone the body so the live page layout isn't affected. Neutralise any
  // off-screen positioning (consumers often set `position:absolute; left:-10000px`
  // on their source element to hide it; if we keep that on the clone, it lives
  // outside the wrapper's box and html2canvas captures nothing).
  const bodyClone = element.cloneNode(true);
  bodyClone.style.position = 'static';
  bodyClone.style.left = '';
  bodyClone.style.top = '';
  bodyClone.style.right = '';
  bodyClone.style.bottom = '';
  bodyClone.style.transform = '';
  wrapper.appendChild(bodyClone);

  if (!skipLastPage) {
    wrapper.appendChild(_buildDefaultLastPageFooter({ skipAiDisclaimer }));
  }
  document.body.appendChild(wrapper);

  try {
    // Wait for layout, fonts, images.
    await new Promise((r) => setTimeout(r, 600));

    const html2canvas = (await import('html2canvas')).default;
    const { jsPDF } = await import('jspdf');

    const SCALE = 2;
    const MAX_CANVAS_DIMENSION = 11000;
    const PDF_PAGE_W_MM = 210;
    const PDF_PAGE_H_MM = 297;
    const MARGIN = { top: 10, left: 10, right: 10, bottom: 15 };
    const CONTENT_W_MM = PDF_PAGE_W_MM - MARGIN.left - MARGIN.right;
    const CONTENT_H_MM = PDF_PAGE_H_MM - MARGIN.top - MARGIN.bottom;

    const elW = wrapper.scrollWidth || 900;
    const elH = wrapper.scrollHeight || 2000;
    const pxPerMm = elW / CONTENT_W_MM;
    const sliceFromPage = Math.floor(CONTENT_H_MM * pxPerMm);
    const sliceFromCanvas = Math.floor(MAX_CANVAS_DIMENSION / SCALE);
    const sliceHeightPx = Math.max(
      200,
      Math.min(sliceFromPage, sliceFromCanvas),
    );

    const pdf = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });

    // If we prepended a cover, capture it as its own page and force a page
    // break before the body. html2canvas ignores the cover's CSS
    // `page-break-after`, so we drive the page boundary manually here.
    let bodyStartPx = 0;
    if (!skipCover && wrapper.firstElementChild) {
      const coverEl = wrapper.firstElementChild;
      const coverHeightPx = coverEl.offsetHeight || coverEl.scrollHeight;
      if (coverHeightPx > 0) {
        const coverCanvas = await html2canvas(wrapper, {
          scale: SCALE,
          useCORS: true,
          logging: false,
          letterRendering: true,
          allowTaint: true,
          windowWidth: elW,
          windowHeight: elH,
          y: 0,
          height: coverHeightPx,
          scrollY: 0,
          backgroundColor: '#ffffff',
        });
        const coverImg = coverCanvas.toDataURL('image/jpeg', 0.95);
        const coverHMm = Math.min(coverHeightPx / pxPerMm, CONTENT_H_MM);
        pdf.addImage(coverImg, 'JPEG', MARGIN.left, MARGIN.top, CONTENT_W_MM, coverHMm);
        pdf.addPage();
        bodyStartPx = coverHeightPx;
      }
    }

    // Body is split into SEGMENTS at `.page-break` markers, then each segment
    // is sliced by sliceHeightPx for canvas-size safety. This honours the
    // CSS `page-break-before` hint that html2canvas would otherwise ignore,
    // so consumers don't get long blank stretches at the bottom of pages.
    const pageBreakOffsets = Array.from(wrapper.querySelectorAll('.page-break'))
      .map((el) => el.offsetTop)
      .filter((y) => Number.isFinite(y) && y > bodyStartPx && y < elH)
      .sort((a, b) => a - b);

    const segments = [];
    let segStart = bodyStartPx;
    for (const y of pageBreakOffsets) {
      if (y > segStart) segments.push({ start: segStart, end: y });
      segStart = y;
    }
    if (segStart < elH) segments.push({ start: segStart, end: elH });

    // Segments slightly taller than one page produce a tiny "remnant" final slice
    // (a page with 1-5% content at top and 95% whitespace below) — what users
    // perceive as a blank page. Within this tolerance, render the whole segment
    // on a single page scaled to fit. Beyond it, fall back to multi-slice so
    // we don't squash content beyond recognition.
    const SCALE_TO_FIT_TOLERANCE = 1.3;  // up to 30% over → one page (≤23% vertical squish)

    const captureSlice = async (yOffset, sliceH) => {
      const canvas = await html2canvas(wrapper, {
        scale: SCALE,
        useCORS: true,
        logging: false,
        letterRendering: true,
        allowTaint: true,
        windowWidth: elW,
        windowHeight: elH,
        y: yOffset,
        height: sliceH,
        scrollY: -yOffset,
        backgroundColor: '#ffffff',
      });
      return canvas.toDataURL('image/jpeg', 0.95);
    };

    let firstBodySlice = true;
    for (const seg of segments) {
      const segHeight = seg.end - seg.start;
      if (segHeight <= 0) continue;

      if (segHeight <= sliceHeightPx * SCALE_TO_FIT_TOLERANCE) {
        // Render the whole segment as ONE page. If it overflows slightly, the
        // image is scaled down to CONTENT_H_MM (vertical-only squish).
        const imgData = await captureSlice(seg.start, segHeight);
        if (!firstBodySlice) pdf.addPage();
        firstBodySlice = false;
        const naturalHMm = segHeight / pxPerMm;
        const renderedHMm = Math.min(naturalHMm, CONTENT_H_MM);
        pdf.addImage(imgData, 'JPEG', MARGIN.left, MARGIN.top, CONTENT_W_MM, renderedHMm);
        continue;
      }

      // Tall segment — multi-slice as before, no squishing.
      const segSlices = Math.ceil(segHeight / sliceHeightPx);
      for (let i = 0; i < segSlices; i++) {
        const yOffset = seg.start + i * sliceHeightPx;
        const sliceH = Math.min(sliceHeightPx, seg.end - yOffset);
        if (sliceH <= 0) break;
        const imgData = await captureSlice(yOffset, sliceH);
        if (!firstBodySlice) pdf.addPage();
        firstBodySlice = false;
        const sliceHMm = sliceH / pxPerMm;
        pdf.addImage(imgData, 'JPEG', MARGIN.left, MARGIN.top, CONTENT_W_MM, sliceHMm);
      }
    }

    // Per-page footer: tenant name (left) + 'Page X of Y' (right).
    const totalPages = pdf.internal.getNumberOfPages();
    const stampedName = (tenantName || '').substring(0, 80);
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(8);
    pdf.setTextColor(107, 114, 128);
    for (let p = 1; p <= totalPages; p++) {
      pdf.setPage(p);
      if (stampedName) {
        pdf.text(stampedName, MARGIN.left, PDF_PAGE_H_MM - 5);
      }
      pdf.text(
        `Page ${p} of ${totalPages}`,
        PDF_PAGE_W_MM - MARGIN.right,
        PDF_PAGE_H_MM - 5,
        { align: 'right' },
      );
    }

    pdf.save(filename);
  } finally {
    if (wrapper.parentElement) wrapper.parentElement.removeChild(wrapper);
    if (style.parentElement) style.parentElement.removeChild(style);
  }
}
